import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { IRecipeIngredient, IRecipeTag } from "../../types/recipe";

interface IEditDataTags {
    old: IRecipeTag[];
    new: string[];
}

export interface IEditData {
    name: string;
    description: string;
    ingredients: IRecipeIngredient[];
    procedure: string[];
    tags: IEditDataTags;
}

export const initialEditData: IEditData = {
    name: "",
    description: "",
    ingredients: [],
    procedure: [],
    tags: { old: [], new: [] },
};

const editDataSlice = createSlice({
    name: "editData",
    initialState: initialEditData,
    reducers: {
        setField: (state, action: PayloadAction<{ field: "name" | "description"; value: string }>) => {
            state[action.payload.field] = action.payload.value as any;
        },

        addIngredient: (state, action: PayloadAction<IRecipeIngredient>) => {
            state.ingredients.push({
                ingredient: action.payload.ingredient,
                name: action.payload.name,
                amount: action.payload.amount,
            });
        },

        removeIngredient: (state, action: PayloadAction<number>) => {
            state.ingredients.splice(action.payload, 1);
        },

        updateIngredientAmount: (state, action: PayloadAction<{ index: number; value: number }>) => {
            state.ingredients[action.payload.index] = {
                ...state.ingredients[action.payload.index],
                amount: action.payload.value,
            };
        },

        addOldTag: (state, action: PayloadAction<IRecipeTag>) => {
            state.tags.old.push(action.payload);
        },

        addNewTag: (state, action: PayloadAction<string>) => {
            state.tags.new.push(action.payload);
        },

        removeOldTag: (state, action: PayloadAction<string>) => {
            state.tags.old = state.tags.old.filter((t) => t.tag.name !== action.payload);
        },

        removeNewTag: (state, action: PayloadAction<string>) => {
            state.tags.new = state.tags.new.filter((t) => t !== action.payload);
        },

        syncProcedure: (state, action: PayloadAction<string[]>) => {
            const newProcedures = [...action.payload];

            state.procedure = newProcedures;
        },

        setProcedure: (state, action: PayloadAction<{ index: number; value: string }>) => {
            const { index, value } = action.payload;
            if (index >= 0 && index < state.procedure.length) {
                state.procedure[index] = value;
            }
        },
        addProcedure: (state) => {
            state.procedure.push("");
        },
        removeProcedure: (state, action: PayloadAction<number>) => {
            state.procedure.splice(action.payload, 1);
        },

        reset: (_state, action: PayloadAction<IEditData>) => {
            return action.payload;
        },
    },
});

export const {
    setField,
    addIngredient,
    removeIngredient,
    updateIngredientAmount,
    addOldTag,
    addNewTag,
    removeOldTag,
    removeNewTag,
    syncProcedure,
    setProcedure,
    addProcedure,
    removeProcedure,
    reset,
} = editDataSlice.actions;

export default editDataSlice.reducer;
